<script>
import { mapActions } from "vuex";

import moment from "@/utils/moment";
import professionalRegisterFlowMixin from "@/mixins/professionalRegisterFlowMixin";
import documentMixin from "@/mixins/documentMixin";

export default {
  mixins: [professionalRegisterFlowMixin],

  props: {
    professional: {
      type: Object,
      required: true
    },
    events: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      requestBackgroundCheckPreloader: false,
      denyAccessPreloader: false,
      approvedBackgroundCheckPreloader: false,
      checkBackgroundCheckOnCAFPreloader: false,
      createProfessionalPGAOnGatewayPreloader: false
    };
  },

  methods: {
    ...mapActions("management", [
      "requestBackgroundCheck",
      "denyAccess",
      "succesBackgroundCheck",
      "createPGAOnGateway"
    ]),
    ...mapActions("documents", ["checkDocumentOnDataAssuranceService"]),

    formattedDate(datetime) {
      return moment(datetime).format("DD MMMM YYYY HH:mm:ss");
    },

    async requestProfessionalBackgroundCheck() {
      this.requestBackgroundCheckPreloader = true;

      try {
        const response = await this.requestBackgroundCheck(
          this.professional.id
        );

        if (response.status === 200) {
          this.$toast.error("Análise dos antecedentes solicitada!");
        } else {
          this.$toast.error(
            "Não foi possível solicitar a análise dos antecedentes no CAF!"
          );
        }
      } catch (e) {
        this.$toast.error(e);
      }

      this.requestBackgroundCheckPreloader = false;
      this.$emit("refresh");
    },

    async createProfessionalPGAOnGateway() {
      this.createProfessionalPGAOnGatewayPreloader = true;

      try {
        const response = await this.createPGAOnGateway(
          this.professional.account.id
        );

        if (response.status === 200) {
          this.$toast.error(
            "Solicitação para criar conta no meio de pagamento enviado!"
          );
        } else {
          this.$toast.error(
            "Não foi possível enviar a solicitação para criar a conta do profissional no meio de pagamento!"
          );
        }
      } catch (e) {
        this.$toast.error(e);
      }

      this.createProfessionalPGAOnGatewayPreloader = false;
      this.$emit("refresh");
    },

    async denyAccess() {
      this.denyAccessPreloader = true;

      try {
        const response = await this.denyAccess(this.professional.id);

        if (response.status === 200) {
          this.$toast.success("Profissional está com acesso negado!");
        } else {
          this.$toast.error("Não foi possível negar o acesso do profissional!");
        }
      } catch (e) {
        this.$toast.error(e);
      }

      this.denyAccessPreloader = false;
      this.$emit("refresh");
    },

    async approveBackgroundCheck() {
      this.approvedBackgroundCheckPreloader = true;

      try {
        const response = await this.succesBackgroundCheck(this.professional.id);

        if (response.status === 200) {
          this.$toast.success("Antecedentes do profissional foram aprovados!");
        } else {
          this.$toast.error(
            "Não foi possível aprovar os antecedentes do profissional!"
          );
        }
      } catch (e) {
        this.$toast.error(e);
      }

      this.approvedBackgroundCheckPreloader = false;
      this.$emit("refresh");
    },

    async checkDocOnDataAssuranceService() {
      this.checkBackgroundCheckOnCAFPreloader = true;

      const document = this.getBackgroundCheckDocument();
      const reportId = documentMixin.methods.reportIdByDocumentType(
        document.document_type
      );

      try {
        const response = await this.checkDocumentOnDataAssuranceService({
          data_assurance_report_id: reportId,
          doc_external_id: document.external_id
        });
        if (response.status === 200) {
          this.$toast.success(
            "Atualização junto ao CAF realizada com sucesso!"
          );
        } else {
          this.$toast.error(
            "Tivemos um erro ao atualizar os antecedentes junto ao CAF :/"
          );
        }
      } catch (error) {
        this.$toast.error(error);
      }

      this.checkBackgroundCheckOnCAFPreloader = false;
      this.$emit("refresh");
    },

    getBackgroundCheckDocument() {
      return this.professional.account.documents.find(
        doc =>
          documentMixin.methods.isBackgroundCheckDocument(doc.document_type) &&
          doc.external_id != null
      );
    }
  },

  computed: {
    isFailedBackgroundCheckRequest: function() {
      return this.professional.status === "failed_background_check_request";
    },

    isBackgroundCheckFailed: function() {
      return this.professional.status === "background_check_failed";
    },

    isWaitingBackgroundCheck: function() {
      return this.professional.status === "waiting_background_check";
    },

    isBackgroundCheckSuccess: function() {
      return this.professional.status === "background_check_success";
    },

    isProcessingDocuments: function() {
      const documents = this.professional.account.documents.filter(doc =>
        documentMixin.methods.isBackgroundCheckDocument(doc.document_type)
      );

      return documents.some(document => document.status === "processing");
    }
  }
};
</script>

<template>
  <div class="body">
    <div class="status-details">
      <div class="description">
        Nessa etapa os antecedentes do profissional serão/estão sendo
        verificados.
      </div>

      <div class="events">
        <ul>
          <li v-for="event in events">
            <b>{{ eventName(event.event_type) | getStatusName }}</b>

            <span>{{ formattedDate(event.created_at) }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="actions">
      <v-btn
        color="primary"
        small
        v-if="isFailedBackgroundCheckRequest"
        :loading="requestBackgroundCheckPreloader"
        @click="requestProfessionalBackgroundCheck"
      >
        Solicitar verificação dos antecedentes
      </v-btn>

      <v-btn
        color="primary"
        small
        v-if="isWaitingBackgroundCheck && isProcessingDocuments"
        :loading="checkBackgroundCheckOnCAFPreloader"
        @click="checkDocOnDataAssuranceService"
      >
        Atualizar status dos antecedentes no CAF
      </v-btn>

      <v-btn
        v-if="isWaitingBackgroundCheck && !isProcessingDocuments"
        color="success"
        small
        :loading="approvedBackgroundCheckPreloader"
        @click="approveBackgroundCheck"
      >
        Aprovar os antecedentes
      </v-btn>

      <v-btn
        color="primary"
        small
        v-if="isBackgroundCheckSuccess"
        :loading="createProfessionalPGAOnGatewayPreloader"
        @click="createProfessionalPGAOnGateway"
      >
        Criar conta do profissional no meio de pagamento
      </v-btn>

      <div v-if="isBackgroundCheckFailed">
        <v-btn
          color="error"
          small
          :loading="denyAccessPreloader"
          :disabled="
            requestBackgroundCheckPreloader || approvedBackgroundCheckPreloader
          "
          @click="denyAccess"
        >
          Negar o acesso
        </v-btn>

        <v-btn
          color="success"
          small
          :loading="approvedBackgroundCheckPreloader"
          :disabled="requestBackgroundCheckPreloader || denyAccessPreloader"
          @click="approveBackgroundCheck"
        >
          Aprovar os antecedentes
        </v-btn>

        <v-btn
          color="primary"
          small
          :loading="requestBackgroundCheckPreloader"
          :disabled="denyAccessPreloader || approvedBackgroundCheckPreloader"
          @click="requestProfessionalBackgroundCheck"
        >
          Solicitar nova verificação dos antecedentes
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.content {
  display: flex;
  justify-content: space-between;
}

.description {
  max-width: 35%;
  text-align: justify;
  display: flex;
  align-items: center;
}
</style>

<style scoped>
.body {
  padding: 16px;
}

.status-details {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: solid #424242 1px;
}

.description {
  width: 35%;
  text-align: justify;
}

.events {
  width: 60%;
  text-decoration: none;
}

li {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 4px 8px;
  list-style: none;
}

li:nth-child(even) {
  background: #eee;
}

.actions {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.action {
  display: flex;
  flex-direction: column;
}

span {
  font-size: 11px;
}
</style>



// WEBPACK FOOTER //
// src/components/Professionals/Edit/Tabs/Register/Steps/BackgroundCheckValidation.vue